<template>
  <div class="text-primary relative z-0 flex min-h-screen flex-col">
    <div class="sticky-menu">
      <div
        class="no-print bg-[#FFC42C] py-0.5 text-center text-[#0D176A]"
        v-if="activePromotion.isActive"
      >
        <b>{{ activePromotion.title }}</b> {{ activePromotion.description }}
        <span
          class="cursor-pointer rounded-lg border px-1"
          @click="copyToClipboard(activePromotion.promoCode)"
          >{{ activePromotion.promoCode }}</span
        >
        |
        <a
          :href="getLandingPageUrl(activePromotion.landingPagePath)"
          class="underline"
          >Check</a
        >
      </div>

      <div class="no-print w-full px-2 xs:px-4 md:px-6 xl:px-[94px]">
        <div class="max-w-8xl mx-auto w-full">
          <nav
            class="relative z-[100] flex flex-wrap items-center justify-between py-2"
            v-if="!$route.meta.hideNavbar"
          >
            <div class="flex items-center space-x-20 lg:pl-1 2xl:space-x-28">
              <a :href="getLandingPageUrl()">
                <img
                  src="@/assets/images/logo/default.svg"
                  class="w-[137px] md:w-[200px] lg:pb-1 xl:w-[243px]"
                  loading="lazy"
                  alt="getlabtest-logo"
                />
              </a>

              <div
                class="no-print order-3 hidden w-full flex-wrap items-center space-y-2 pt-8 md:order-2 md:w-auto md:space-x-4 md:space-y-0 md:pt-0 lg:flex xl:space-x-7 2xl:space-x-[40px]"
              >
                <a
                  :href="getLandingPageUrl('lab-test-list/tests')"
                  class="tracking-nav transition-ease-in-out text-nav font-normal uppercase text-primary-100 hover:text-primary-light [&.router-link-active]:text-primary-light"
                >
                  {{ $t("layouts.catalog") }}
                </a>

                <a
                  :href="getLandingPageUrl('lab-test-list/packages')"
                  class="tracking-nav transition-ease-in-out text-nav font-normal uppercase text-primary-100 hover:text-primary-light [&.router-link-active]:text-primary-light"
                >
                  {{ $t("layouts.packages") }}
                </a>

                <a
                  :href="getLandingPageUrl('locations')"
                  class="tracking-nav transition-ease-in-out text-nav font-normal uppercase text-primary-100 hover:text-primary-light [&.router-link-active]:text-primary-light"
                >
                  {{ $t("layouts.collection_points") }}
                </a>

                <router-link
                  :to="{ name: 'Results' }"
                  class="tracking-nav transition-ease-in-out text-nav font-normal uppercase text-primary-100 hover:text-primary-light [&.router-link-active]:text-primary-light"
                >
                  {{ $t("layouts.receive_results") }}
                </router-link>

                <router-link
                  :to="{ name: 'WeightLossProgress' }"
                  class="tracking-nav transition-ease-in-out text-nav font-normal uppercase text-primary-100 hover:text-primary-light [&.router-link-active]:text-primary-light"
                >
                  {{ $t("layouts.weight_loss") }}
                </router-link>

                <a
                  :href="
                    isEnabledFeature('new_offers_page')
                      ? getLandingPageUrl('offers')
                      : getLandingPageUrl(
                          'offer/invest-in-the-health-of-your-workforce-today'
                        )
                  "
                  class="tracking-nav transition-ease-in-out text-nav font-normal uppercase text-primary-100 hover:text-primary-light [&.router-link-active]:text-primary-light"
                >
                  {{ $t("layouts.offer") }}
                </a>

                <a
                  :href="getLandingPageUrl('news/getlabtest-news')"
                  class="tracking-nav transition-ease-in-out text-nav font-normal uppercase text-primary-100 hover:text-primary-light [&.router-link-active]:text-primary-light"
                >
                  Blog
                </a>
              </div>
            </div>

            <div
              class="no-print order-2 flex items-center space-x-2 xs:w-fit xs:space-x-4 md:order-3 lg:space-x-2"
            >
              <Dropdown />

              <button
                @click.prevent="onClickGoToCart"
                @mousedown.middle.prevent="onClickGoToCart"
                class="transition-ease-in-out flex items-center rounded-lg font-semibold hover:opacity-70"
              >
                <div class="relative">
                  <img
                    src="@/assets/images/base/icons/shopping_cart.svg"
                    class="object-cover xs:mx-1 lg:mx-2"
                    loading="lazy"
                  />
                  <div
                    v-if="cart.length > 0"
                    class="absolute -top-[5px] inline-flex h-[17px] w-auto min-w-[17px] items-center justify-center rounded-full bg-primary-main text-[10px] text-white xs:right-[2px]"
                  >
                    {{ cart.length }}
                  </div>
                  <div
                    class="hidden text-center text-[10px] font-bold text-primary-main group-hover:brightness-50 lg:block"
                  >
                    <span v-if="cart.length > 0">
                      {{ totalPriceFormatted }}
                    </span>
                  </div>
                </div>
              </button>

              <DropdownNavigation class="lg:hidden" />
            </div>
          </nav>
        </div>
      </div>

      <GeneralQuizBar />
      <WeightLossQuizBar />
    </div>

    <div
      class="max-w-8xl relative flex w-full flex-grow flex-col self-center px-4 pb-[100px] pt-4 md:px-6 md:pt-10 lg:pb-[100px] 2xl:px-0"
      :class="layoutClasses"
    >
      <BreadCrumbs class="no-print" />

      <slot>
        <router-view v-slot="{ Component }">
          <transition name="fade" mode="out-in" appear>
            <component :is="Component" />
          </transition>
        </router-view>
      </slot>

      <div class="flex justify-end pt-5"></div>
    </div>
    <Footer />

    <DoctorAskForAccessToHistoryModal />
    <AddCurrentPatientModal />
  </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from "vuex";
import landingPageMixin from "@/mixins/landingPageMixin";
import StickyElement from "vue-sticky-element";
import Button from "@/components/Core/ButtonBase.vue";
import Dropdown from "@/components/Layouts/User/Dropdown";
import DropdownNavigation from "@/components/Layouts/User/DropdownNavigation";
import BreadCrumbs from "@/components/Core/BreadCrumbs.vue";
import Footer from "@/components/Core/Footer";
import DoctorAskForAccessToHistoryModal from "@/components/Patient/Modal/DoctorAskForAccessToHistoryModal.vue";
import AddCurrentPatientModal from "@/components/Patient/Modal/AddCurrentPatientModal.vue";
import CoreButtonBase from "@/components/Core/ButtonBase.vue";
import currencyFormatterMixin from "@/mixins/currencyFormatterMixin";
import GeneralQuizBar from "@/components/Bars/GeneralQuizBar.vue";
import WeightLossQuizBar from "@/components/Bars/WeightLossQuizBar.vue";
import { getActivePromotion } from "@/utils/promotionConfig";

export default {
  name: "LayoutsUser",
  components: {
    WeightLossQuizBar,
    GeneralQuizBar,
    CoreButtonBase,
    AddCurrentPatientModal,
    Button,
    Dropdown,
    DropdownNavigation,
    BreadCrumbs,
    Footer,
    StickyElement,
    DoctorAskForAccessToHistoryModal,
  },
  inject: ["wsEcho"],
  mixins: [landingPageMixin, currencyFormatterMixin],
  data() {
    return {
      showDiscount: false,
      modalVisible: true,
    };
  },
  computed: {
    activePromotion() {
      return getActivePromotion();
    },
    layoutClasses() {
      const isCatchAllRoute = this.$route.matched.some(
        (record) => record.path === "/:catchAll(.*)"
      );
      if (isCatchAllRoute) {
        return "bg_404";
      }
      if (this.$route.name != "Doctors") return "overflow-hidden";
      return "";
    },
    totalPriceFormatted() {
      return this.formatCurrency(this.totalPrice);
    },
    id() {
      return this.$route.params.id;
    },
    ...mapGetters({
      currentUser: "user/getCurrentUser",
      currentUserRole: "user/getCurrentUserRole",
      cart: "cart/getCart",
      doctors: "user/getDoctors",
      totalPrice: "cart/getTotalPrice",
      isEnabledFeature: "features/isEnabled",
      patients: "checkout/getPatients",
      selectedFacility: "cart/getSelectedFacility",
      discountCode: "cart/getDiscountCode",
      hasExaminations: "cart/hasExaminations",
    }),
  },
  methods: {
    copyToClipboard(promoCode) {
      navigator.clipboard.writeText(promoCode);
      this.notifySuccess("Code copied to clipboard!");
    },
    onClickGoToCart() {
      this.$router.push({ name: "Cart" });
    },
    initNotificationChannelListener() {
      if (this.wsEcho) {
        this.wsEcho
          .private(`notification.${this.currentUser.id}`)
          .listen("AskForAccessToHistory", (response) => {
            this.updateOrCreateDoctor(response);
            this.openModal("doctorAskForAccessToHistoryModal");
          })
          .listen("UserChangedStatus", (response) => {
            if (response.status === "blocked") {
              this.wsEcho.leave(`notification.${this.currentUser.id}`);
              this.resetUserAfterLogOut();
              this.goToLandingPage("error/403", { blocked: 1 });
            }
          });
      }
    },
    ...mapMutations({
      openModal: "modals/openModal",
    }),
    ...mapActions({
      calculatePrice: "cart/calculatePrice",
      updateExaminationsPrices: "cart/updateExaminationsPrices",
      fetchNursingServices: "examinations/fetchNursingServices",
      realizeDiscountCode: "cart/realizeDiscountCode",
      checkOrderAutoDiscountCode: "cart/checkOrderAutoDiscountCode",
      updateOrCreateDoctor: "user/updateOrCreateDoctor",
      resetUserAfterLogOut: "auth/resetUserAfterLogOut",
    }),
  },
  mounted() {
    // prevent error Cannot read properties of null (reading 'clientHeight') vue-sticky-element
    if (!this.id) {
      Promise.all([
        this.fetchNursingServices(),
        this.updateExaminationsPrices(),
      ]).then(() => {
        if (this.discountCode) {
          this.realizeDiscountCode(this.discountCode.code);
        } else if (this.hasExaminations) {
          this.checkOrderAutoDiscountCode();
        }
      });

      this.showDiscount = true;
    }

    if (this.currentUser?.id) {
      this.initNotificationChannelListener();

      this.$gtm.trackEvent({
        user_id: this.currentUser.id,
      });
    }
  },
  head() {
    return {
      title: "GetLabTest",
      titleTemplate: "%s - GLT",
    };
  },
};
</script>

<style lang="scss" scoped>
.vue-sticky-element--show {
  z-index: 65;
}
.tab-button {
  @apply mr-8 flex w-auto cursor-pointer items-center border-b border-solid pb-1 text-xs uppercase tracking-[1px] hover:border-b-light hover:font-semibold sm:my-5 [&.router-link-active]:border-b-light;
}

.sticky-menu {
  @apply sticky top-0 z-[101] border-b bg-white bg-opacity-80 print:border-none print:shadow-none;
  border-image: linear-gradient(
      to left,
      rgba(255, 255, 255, 0) 0%,
      rgba(70, 34, 197, 0.2) 10%,
      rgba(70, 34, 197, 0.2) 90%,
      rgba(255, 255, 255, 0) 100%
    )
    1 / 1 / 0 stretch;
  box-shadow: rgba(70, 34, 197, 0.69) 0 0.5rem 1.5rem -1.25rem;

  &:before {
    @apply absolute inset-0 z-[-1] backdrop-blur-[1.75rem] content-[""];
  }
}
</style>
